import { type ReactNode, useState } from "react"
import { Link } from "react-router-dom"
import { cn } from "@/lib/utils.js"
import { count } from "./format.js"

export interface RankedRow {
  readonly key: string
  readonly label: string
  /** The identifier beneath the label — a code, a key — and anything that qualifies it. */
  readonly detail: ReactNode
  readonly value: number
  /** The value as a part of whatever the list is a part of, already written. */
  readonly share: string | null
  /** The list this row summarises. Every row leads to the decisions behind it. */
  readonly to: string
  readonly title?: string
}

/**
 * A ranked list with a bar per row: one series, one colour, the number at the
 * end of every bar. **Each row is the link to the decisions it counts** — a
 * figure on this screen that cannot be followed to its rows is a figure nobody
 * can act on, which is the dashboard this panel refused to build.
 */
export function RankedBars({
  rows,
  tone,
  empty,
  limit = 6,
}: {
  rows: readonly RankedRow[]
  tone: "prepay" | "neutral"
  empty: string
  limit?: number
}) {
  const [all, setAll] = useState(false)

  if (rows.length === 0) {
    return (
      <p className="rounded-lg border bg-card px-4 py-5 text-sm text-muted-foreground shadow-xs">
        {empty}
      </p>
    )
  }

  const max = Math.max(...rows.map((row) => row.value), 1)
  const shown = all ? rows : rows.slice(0, limit)

  return (
    <div className="rounded-lg border bg-card p-1.5 shadow-xs">
      <ul>
        {shown.map((row) => (
          <li key={row.key}>
            <Link
              to={row.to}
              {...(row.title === undefined ? {} : { title: row.title })}
              className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-5 rounded-md px-3 py-2 no-underline transition-colors hover:bg-muted/50"
            >
              <div className="min-w-0">
                <p className="truncate text-sm text-foreground">{row.label}</p>
                <p className="truncate text-[12px] text-muted-foreground">{row.detail}</p>
                <div
                  aria-hidden="true"
                  className={cn(
                    "mt-1.5 h-1.5 rounded-r-[3px]",
                    tone === "prepay" ? "bg-prepay" : "bg-chart-neutral",
                  )}
                  style={{ width: `${Math.max((row.value / max) * 100, 1.5)}%` }}
                />
              </div>
              <div className="text-right">
                <p className="text-[15px] font-semibold leading-5 tabular-nums text-foreground">
                  {count(row.value)}
                </p>
                {row.share !== null && (
                  <p className="text-[12px] tabular-nums text-muted-foreground">{row.share}</p>
                )}
              </div>
            </Link>
          </li>
        ))}
      </ul>
      {rows.length > limit && (
        <button
          type="button"
          onClick={() => setAll(!all)}
          className="w-full rounded-md px-3 py-1.5 text-left text-[13px] text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground"
        >
          {all ? "Pokaż mniej" : `Pokaż wszystkie (${rows.length})`}
        </button>
      )}
    </div>
  )
}
